/* ---------------------------------------------------------------------------
   Construction Monitoring: one structure's progress photographs, in one place.

   Every photograph attached to a construction entry, read three ways:

     · by calendar — a month at a time, each day showing what was photographed;
     · along the days — the dates left to right, that day's photographs stacked
       under each;
     · as thumbnails — a grid, grouped by month, newest or oldest first.

   Each opens the same enlarged view, which steps through all of them in date
   order, so a reader who starts from a day in the calendar can walk forward
   through the job without going back to the page.

   THE PHOTOGRAPH IS THE ENTRY'S OWN. Nothing here is a second store of
   pictures: a photograph is `ProgressEntry.hasPhoto`, fetched by id exactly as
   the Works list fetches its thumbnail (src/screens/works.ts), and its caption
   is the entry's activity, component and note.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type Child } from '../core/dom.ts';
import { actions, type PhotoLayout, type State } from '../core/store.ts';
import * as api from '../core/api.ts';
import { emptyState, scrim, seg } from '../components/widgets.ts';
import { panelButton } from '../components/panel.ts';
import { photoStage } from '../components/photoZoom.ts';
import { findBridge, prettyDate } from '../data/derive.ts';
import { activityLabel } from '../data/activities.ts';
import { progressStateTag } from './works.ts';
import { weatherLabel } from '../data/siteReturn.ts';
import { weatherChip, weatherGlyph, weatherSky, wxAttrs } from '../components/weather.ts';
import type { ProgressEntry, SiteReturn } from '../data/types.ts';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The selected structure's photographed entries, oldest first. */
function photographed(s: Readonly<State>): ProgressEntry[] {
  return s.progress
    .filter((e) => e.hasPhoto && !e.pending && e.bridgeId === s.selectedId)
    .sort((a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.createdAt.localeCompare(b.createdAt));
}

function photoSrc(e: ProgressEntry): string {
  /* TEMPORARY — the FWO demonstration; delete the `??` with src/data/fwo-demo.ts,
     as in works.ts. */
  return e.photoUrl ?? api.progressPhotoUrl(e.bridgeId, e.id);
}

function thumbSrc(e: ProgressEntry): string {
  return e.photoThumbUrl ?? photoSrc(e);
}

function caption(e: ProgressEntry): string {
  return e.component ? `${activityLabel(e.activity)} — ${e.component}` : activityLabel(e.activity);
}

function altText(e: ProgressEntry): string {
  return `Photograph of ${caption(e)} on ${prettyDate(e.occurredOn)}`;
}

/** Photographs by day, keyed by ISO date, each day oldest first. */
function byDay(photos: readonly ProgressEntry[]): Map<string, ProgressEntry[]> {
  const days = new Map<string, ProgressEntry[]>();
  for (const e of photos) {
    const day = e.occurredOn.slice(0, 10);
    const list = days.get(day);
    if (list) list.push(e);
    else days.set(day, [e]);
  }
  return days;
}

/** The selected structure's daily returns, by day. */
function returnsByDay(s: Readonly<State>): Map<string, SiteReturn> {
  return new Map(s.siteReturns.filter((r) => r.bridgeId === s.selectedId).map((r) => [r.returnedOn, r]));
}

function monthName(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

function weekday(iso: string): string {
  /* Monday first, as the calendar is drawn. */
  return WEEKDAYS[(new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7]!;
}

/* --------------------------- the page --------------------------- */

export function progressPhotos(s: Readonly<State>): HTMLElement {
  if (s.progressRead === 'unreachable') return emptyState('The construction record could not be read.');
  if (s.progressRead === 'unknown') return emptyState('The construction record has not been read yet.');

  const photos = photographed(s);
  if (photos.length === 0) return emptyState('No photograph has been attached to a construction entry on this structure.');

  let body: HTMLElement;
  switch (s.photoLayout) {
    case 'calendar':
      body = calendar(s, photos);
      break;
    case 'timeline':
      body = timeline(photos);
      break;
    case 'grid':
      body = grid(photos, s.photoNewestFirst);
      break;
  }
  return h('div', { class: ['pphotos', `pphotos--${s.photoLayout}`] }, body);
}

/** The panel header's controls: the layout, and — for the grid — the order. */
export function progressPhotoActions(s: Readonly<State>): HTMLElement[] {
  const layouts: { layout: PhotoLayout; label: string }[] = [
    { layout: 'calendar', label: 'Calendar' },
    { layout: 'timeline', label: 'Timeline' },
    { layout: 'grid', label: 'Thumbnails' },
  ];
  return [
    s.photoLayout === 'grid'
      ? panelButton({
          icon: ICONS.arrowUp,
          text: s.photoNewestFirst ? 'Newest first' : 'Oldest first',
          title: 'Reverse the order of the thumbnails',
          onclick: () => actions.togglePhotoOrder(),
        })
      : null,
    seg(
      layouts.map((l) => ({
        label: l.label,
        on: s.photoLayout === l.layout,
        onClick: () => actions.setPhotoLayout(l.layout),
      })),
    ),
  ].filter((x): x is HTMLElement => x !== null);
}

/** One photograph as a button that enlarges it. */
function tile(e: ProgressEntry, cls: string, label?: Child): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: cls,
      title: `${caption(e)} · ${prettyDate(e.occurredOn)}`,
      onclick: () => actions.openProgressPhoto(e.id),
    },
    h('img', { src: thumbSrc(e), alt: altText(e), loading: 'lazy', draggable: 'false' }),
    label ?? null,
  );
}

/* --------------------------- by calendar --------------------------- */

function calendar(s: Readonly<State>, photos: readonly ProgressEntry[]): HTMLElement {
  const days = byDay(photos);
  const months = [...new Set(photos.map((e) => e.occurredOn.slice(0, 7)))];
  const first = months[0]!;
  const last = months[months.length - 1]!;
  const month = s.photoMonth !== null && s.photoMonth >= first && s.photoMonth <= last ? s.photoMonth : last;

  const start = `${month}-01`;
  const lead = (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7;
  const [y, m] = month.split('-').map(Number) as [number, number];
  const length = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const inMonth = photos.filter((e) => e.occurredOn.startsWith(month)).length;
  const today = new Date().toISOString().slice(0, 10);
  const weather = returnsByDay(s);

  const cells: HTMLElement[] = [];
  for (let i = 0; i < lead; i++) cells.push(h('div', { class: 'pcal__day pcal__day--pad' }));
  for (let d = 1; d <= length; d++) {
    const iso = `${month}-${String(d).padStart(2, '0')}`;
    const list = days.get(iso) ?? [];
    /* The day's weather behind its photographs, where the day has a return. */
    const ret = weather.get(iso);
    cells.push(
      h(
        'div',
        {
          class: ['pcal__day', list.length > 0 && 'pcal__day--has', iso === today && 'pcal__day--today'],
          ...(ret ? wxAttrs(ret.weather) : {}),
        },
        ret ? weatherSky() : null,
        h('span', { class: 'pcal__num num', text: String(d) }),
        ret
          ? h(
              'span',
              { class: 'pcal__wx', title: weatherLabel(ret.weather) },
              h('span', { class: 'pcal__wx-label', text: weatherLabel(ret.weather) }),
              weatherGlyph(ret.weather, 15),
            )
          : null,
        list.length
          ? h(
              'div',
              { class: ['pcal__shots', list.length > 1 && 'pcal__shots--many'] },
              ...list.slice(0, 4).map((e, i) =>
                tile(
                  e,
                  'pcal__shot',
                  i === 3 && list.length > 4 ? h('span', { class: 'pcal__more', text: `+${list.length - 4}` }) : null,
                ),
              ),
            )
          : null,
      ),
    );
  }
  while (cells.length % 7 !== 0) cells.push(h('div', { class: 'pcal__day pcal__day--pad' }));

  return h(
    'div',
    { class: 'pcal' },
    h(
      'div',
      { class: 'pcal__bar' },
      h(
        'button',
        {
          type: 'button',
          class: 'pcal__step',
          title: 'Previous month with photographs',
          'aria-label': 'Previous month',
          disabled: month <= first,
          onclick: () => actions.setPhotoMonth(months.filter((x) => x < month).pop() ?? first),
        },
        icon(ICONS.chevronRight, { size: 14, width: 1.8, class: 'pcal__back' }),
      ),
      h('h3', { class: 'pcal__month', text: monthName(month) }),
      h(
        'button',
        {
          type: 'button',
          class: 'pcal__step',
          title: 'Next month with photographs',
          'aria-label': 'Next month',
          disabled: month >= last,
          onclick: () => actions.setPhotoMonth(months.find((x) => x > month) ?? last),
        },
        icon(ICONS.chevronRight, { size: 14, width: 1.8 }),
      ),
      h('span', {
        class: 'pcal__count num',
        text: `${inMonth} ${inMonth === 1 ? 'photograph' : 'photographs'} this month · ${photos.length} in all`,
      }),
    ),
    h(
      'div',
      { class: 'pcal__grid', 'aria-label': `Photographs in ${monthName(month)}` },
      ...WEEKDAYS.map((w) => h('div', { class: 'pcal__wd', text: w })),
      ...cells,
    ),
  );
}

/* --------------------------- along the days --------------------------- */

function timeline(photos: readonly ProgressEntry[]): HTMLElement {
  const days = [...byDay(photos)];
  const cols: HTMLElement[] = [];
  let prev: string | null = null;
  for (const [day, list] of days) {
    /* The axis is linear in DATES, not in columns: a stretch with nothing
       photographed is drawn as a gap that says how long it was. */
    if (prev !== null) {
      const gap = Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${prev}T00:00:00Z`)) / 86_400_000) - 1;
      if (gap > 0) cols.push(h('div', { class: 'ptl__gap', style: { width: `${Math.min(64, 10 + gap * 3)}px` }, title: `${gap} ${gap === 1 ? 'day' : 'days'} without a photograph` }));
    }
    const newMonth = prev === null || prev.slice(0, 7) !== day.slice(0, 7);
    cols.push(
      h(
        'section',
        { class: 'ptl__col' },
        h(
          'header',
          { class: 'ptl__head' },
          h('span', { class: 'ptl__month', text: newMonth ? monthName(day.slice(0, 7)) : '' }),
          h('span', { class: 'ptl__dot' }),
          h(
            'span',
            { class: 'ptl__date' },
            h('b', { class: 'num', text: String(Number(day.slice(8, 10))) }),
            ` ${weekday(day)}`,
          ),
        ),
        h(
          'div',
          { class: 'ptl__stack' },
          ...list.map((e) =>
            h(
              'figure',
              { class: 'ptl__fig' },
              tile(e, 'ptl__shot'),
              h('figcaption', { class: 'ptl__cap', text: caption(e) }),
            ),
          ),
        ),
      ),
    );
    prev = day;
  }
  return h('div', { class: 'ptl', 'aria-label': 'Photographs by date' }, h('div', { class: 'ptl__track' }, ...cols));
}

/* --------------------------- as thumbnails --------------------------- */

function grid(photos: readonly ProgressEntry[], newestFirst: boolean): HTMLElement {
  const ordered = newestFirst ? [...photos].reverse() : [...photos];
  const groups = new Map<string, ProgressEntry[]>();
  for (const e of ordered) {
    const month = e.occurredOn.slice(0, 7);
    const list = groups.get(month);
    if (list) list.push(e);
    else groups.set(month, [e]);
  }
  return h(
    'div',
    { class: 'pgrid' },
    ...[...groups].map(([month, list]) =>
      h(
        'section',
        { class: 'pgrid__group' },
        h(
          'h3',
          { class: 'pgrid__month' },
          monthName(month),
          h('span', { class: 'pgrid__n num', text: String(list.length) }),
        ),
        h(
          'div',
          { class: 'pgrid__tiles' },
          ...list.map((e) =>
            tile(
              e,
              'pgrid__tile',
              h(
                'span',
                { class: 'pgrid__label' },
                h('span', { class: 'pgrid__date num', text: prettyDate(e.occurredOn) }),
                h('span', { class: 'pgrid__what', text: activityLabel(e.activity) }),
              ),
            ),
          ),
        ),
      ),
    ),
  );
}

/* --------------------------- enlarged --------------------------- */

/** The photographs the enlarged view steps through, and where the open one is among them. */
function openPosition(s: Readonly<State>): { photos: ProgressEntry[]; at: number } | null {
  if (s.progressPhotoId === null) return null;
  const photos = photographed(s);
  const at = photos.findIndex((e) => e.id === s.progressPhotoId);
  return at < 0 ? null : { photos, at };
}

/** Step the enlarged photograph by one, in date order. False where there is nowhere to go. */
export function stepProgressPhoto(s: Readonly<State>, by: 1 | -1): boolean {
  const open = openPosition(s);
  const next = open?.photos[open.at + by];
  if (!next) return false;
  actions.openProgressPhoto(next.id);
  return true;
}

export function progressPhotoDialog(s: Readonly<State>): HTMLElement | null {
  const b = findBridge(s.selectedId);
  const open = openPosition(s);
  /* The entry can have gone under an open photograph — the record is re-read
     whenever something is saved. Nothing to enlarge, so close. */
  if (b === null || open === null) return null;
  const { photos, at } = open;
  const e = photos[at]!;
  const sameDay = photos.filter((p) => p.occurredOn === e.occurredOn);
  const ret = returnsByDay(s).get(e.occurredOn.slice(0, 10));

  const step = (by: 1 | -1, label: string) =>
    h(
      'button',
      {
        type: 'button',
        class: ['dlg__close', 'pphoto__step', by < 0 && 'pphoto__step--back'],
        title: `${label} photograph (${by < 0 ? '←' : '→'})`,
        'aria-label': `${label} photograph`,
        disabled: !photos[at + by],
        onclick: () => stepProgressPhoto(s, by),
      },
      icon(ICONS.chevronRight, { size: 16, width: 1.8 }),
    );

  return h(
    'div',
    { class: 'dlg dlg--photo', ...scrim(() => actions.closeProgressPhoto()) },
    h(
      'div',
      { class: 'photolb', role: 'dialog', 'aria-modal': 'true', 'aria-label': altText(e) },
      h(
        'div',
        { class: 'photolb__head' },
        h(
          'div',
          { style: 'min-width:0' },
          h('div', { class: 'dlg__kicker', text: `${b.id} · progress photograph ${at + 1} of ${photos.length}` }),
          h('h2', { class: 'photolb__title', text: `${caption(e)} — ${prettyDate(e.occurredOn)}` }),
        ),
        h('span', { class: 'spacer' }),
        step(-1, 'Previous'),
        step(1, 'Next'),
        h(
          'button',
          { type: 'button', class: 'dlg__close', title: 'Close (Esc)', 'aria-label': 'Close', onclick: () => actions.closeProgressPhoto() },
          '×',
        ),
      ),
      photoStage(photoSrc(e), altText(e)),
      h(
        'div',
        { class: 'photolb__foot' },
        h(
          'div',
          { class: 'photolb__meta' },
          e.note
            ? h('div', { class: 'photolb__notes', text: e.note })
            : h('div', { class: 'photolb__notes photolb__notes--none', text: 'No note on this entry.' }),
          h(
            'div',
            { class: 'photolb__when' },
            ret ? weatherChip(ret.weather) : null,
            [
              `${weekday(e.occurredOn)} ${prettyDate(e.occurredOn)}`,
              sameDay.length > 1 ? `${sameDay.indexOf(e) + 1} of ${sameDay.length} that day` : null,
              e.author,
            ]
              .filter(Boolean)
              .join(' · '),
          ),
        ),
        progressStateTag(e.state),
      ),
    ),
  );
}
